import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE, CONTACT } from '../mock';

export default function Seo({
  title,
  description,
  path = '/',
  image = 'https://images.unsplash.com/photo-1487139975590-b4f1dce9b035?auto=format&fit=crop&w=1200&q=80',
  type = 'website',
  keywords,
  schema,
}) {
  const fullTitle = title
    ? (title.includes(SITE.name) ? title : `${title} | ${SITE.name}`)
    : `${SITE.name} | ${SITE.tagline}`;
  const desc = description || SITE.description;
  const url = `${SITE.domain}${path}`;
  const defaultKeywords = 'counseling Chesapeake VA, therapist Chesapeake VA, trauma therapy, EMDR therapy, anxiety therapy, couples counseling, women’s counseling, telehealth therapy Virginia, Hampton Roads counseling';

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      <link rel="canonical" href={url} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      <meta name="author" content={SITE.name} />
      <meta name="geo.region" content="US-VA" />
      <meta name="geo.placename" content="Chesapeake" />

      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
}

export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'MentalHealthCare',
  name: CONTACT.name,
  url: SITE.domain,
  telephone: CONTACT.phone,
  email: CONTACT.email,
  address: {
    '@type': 'PostalAddress',
    streetAddress: CONTACT.address,
    addressLocality: 'Chesapeake',
    addressRegion: 'VA',
    postalCode: '23322',
    addressCountry: 'US',
  },
  openingHours: ['Mo-Fr 09:00-17:00'],
  areaServed: ['Chesapeake, VA', 'Virginia Beach, VA', 'Norfolk, VA', 'Suffolk, VA', 'Portsmouth, VA', 'Hampton Roads'],
  sameAs: [
    'https://www.facebook.com/chescounseling1',
    'https://www.instagram.com/chesapeakecc2018/',
    'https://www.youtube.com/channel/UCV6pGNcw8bKAqJyChujVV6g',
  ],
});

export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.label,
    item: it.to ? `${SITE.domain}${it.to}` : undefined,
  })),
});

export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

export const articleSchema = (post) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.excerpt,
  image: post.cover,
  datePublished: post.date,
  author: { '@type': 'Person', name: post.author },
  publisher: {
    '@type': 'Organization',
    name: CONTACT.name,
    logo: { '@type': 'ImageObject', url: `${SITE.domain}/logo.png` },
  },
});

export const personSchema = (t) => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: t.name,
  jobTitle: `${t.role} — ${t.credentials}`,
  image: t.photo,
  worksFor: { '@type': 'Organization', name: CONTACT.name },
});
